import React from 'react';
import { Box, Typography, Skeleton } from '@mui/material';

interface RippleLoaderProps {
  title?: string;
  subtitle?: string;
  size?: 'small' | 'medium' | 'large';
  fullScreen?: boolean;
  className?: string;
}

const RippleLoader: React.FC<RippleLoaderProps> = ({
  title,
  subtitle,
  size = 'medium',
  fullScreen = false,
  className = '',
}) => {

  // Size configurations
  const sizeConfig = {
    small: {
      container: 60,
      circles: [60, 48, 36],
      center: 24,
      titleVariant: 'body1' as const,
      subtitleVariant: 'body2' as const,
    },
    medium: {
      container: 100,
      circles: [100, 80, 60],
      center: 40,
      titleVariant: 'h6' as const,
      subtitleVariant: 'body2' as const,
    },
    large: {
      container: 140,
      circles: [140, 112, 84],
      center: 56,
      titleVariant: 'h5' as const,
      subtitleVariant: 'body1' as const,
    },
  };

  const config = sizeConfig[size];

  return (
    <Box
      className={`ripple-loader ${className}`}
      sx={{
        height: fullScreen ? '100vh' : '100%',
        display: 'flex',
        flexDirection: 'column',
        justifyContent: 'center',
        alignItems: 'center',
        p: fullScreen ? 3 : 2,
        minHeight: fullScreen ? '100vh' : '100%',
        width: '100%',
        maxWidth: '100%',
        overflow: 'hidden',
        position: 'relative',
      }}
    >
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 2,
          maxWidth: 400,
          textAlign: 'center',
          width: '100%',
          height: '100%',
          minHeight: 'fit-content',
        }}
      >
        {/* Three Pulsing Circles Animation */}
        <Box
          sx={{
            position: 'relative',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            width: config.container,
            height: config.container,
          }}
        >
          {/* First pulsing circle - largest, slowest */}
          <Box
            sx={{
              position: 'absolute',
              width: '100%',
              height: '100%',
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              animation: 'pulse1 2.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              '@keyframes pulse1': {
                '0%': {
                  opacity: 0.1,
                  transform: 'scale(0.8)',
                },
                '50%': {
                  opacity: 0.3,
                  transform: 'scale(1.2)',
                },
                '100%': {
                  opacity: 0,
                  transform: 'scale(1.4)',
                },
              },
            }}
          />

          {/* Second pulsing circle - medium, medium speed */}
          <Box
            sx={{
              position: 'absolute',
              width: '80%',
              height: '80%',
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              animation: 'pulse2 2s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              animationDelay: '0.3s',
              '@keyframes pulse2': {
                '0%': {
                  opacity: 0.15,
                  transform: 'scale(0.8)',
                },
                '50%': {
                  opacity: 0.4,
                  transform: 'scale(1.15)',
                },
                '100%': {
                  opacity: 0,
                  transform: 'scale(1.3)',
                },
              },
            }}
          />

          {/* Third pulsing circle - smallest, fastest */}
          <Box
            sx={{
              position: 'absolute',
              width: '60%',
              height: '60%',
              borderRadius: '50%',
              backgroundColor: 'primary.main',
              animation: 'pulse3 1.5s cubic-bezier(0.4, 0, 0.6, 1) infinite',
              animationDelay: '0.6s',
              '@keyframes pulse3': {
                '0%': {
                  opacity: 0.2,
                  transform: 'scale(0.8)',
                },
                '50%': {
                  opacity: 0.5,
                  transform: 'scale(1.1)',
                },
                '100%': {
                  opacity: 0,
                  transform: 'scale(1.2)',
                },
              },
            }}
          />

          {/* Main loading circle - center */}
          <Skeleton
            variant="circular"
            width={config.center}
            height={config.center}
            sx={{
              position: 'relative',
              zIndex: 4,
              backgroundColor: 'primary.main',
              opacity: 0.9,
              animation: 'spin 1s linear infinite',
              '@keyframes spin': {
                '0%': {
                  transform: 'rotate(0deg)',
                },
                '100%': {
                  transform: 'rotate(360deg)',
                },
              },
            }}
          />
        </Box>

        {/* Loading Text */}
        {(title || subtitle) && (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1, width: '100%' }}>
            {title && (
              <Typography
                variant={config.titleVariant}
                color="text.secondary"
                sx={{
                  fontWeight: 600,
                  fontSize: {
                    xs: size === 'large' ? '1.1rem' : size === 'medium' ? '1rem' : '0.9rem',
                    sm: size === 'large' ? '1.25rem' : size === 'medium' ? '1.1rem' : '1rem',
                  },
                }}
              >
                {title}
              </Typography>
            )}
            {subtitle && (
              <Typography
                variant={config.subtitleVariant}
                color="text.disabled"
                sx={{
                  fontSize: {
                    xs: size === 'large' ? '0.9rem' : size === 'medium' ? '0.8rem' : '0.75rem',
                    sm: size === 'large' ? '1rem' : size === 'medium' ? '0.9rem' : '0.8rem',
                  },
                }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>
        )}
      </Box>
    </Box>
  );
};

export default RippleLoader;
